/**
 * Superadmin-only tenant management and tenant switcher.
 *
 * A tenant is a data/billing boundary (PRD 7.3): every other admin screen
 * only ever sees its own tenant. A superadmin instead switches which
 * tenant's context it operates in by sending X-Tenant-Id (see
 * resolve_principal in app/core/security.py) - this page is where that
 * header gets set, in localStorage, via api.setTenantOverride.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, getTenantOverride, setTenantOverride } from '../lib/api'
import { useIdentity } from '../hooks/useSession'
import type { TenantOut } from '../lib/types'
import { Callout, ErrorCallout } from '../components/Callout'
import { Chip } from '../components/Chips'
import { InfoTip } from '../components/InfoTip'
import { Skeleton } from '../components/Metric'

function CreateTenantForm() {
  const queryClient = useQueryClient()
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [currency, setCurrency] = useState('INR')
  const [isSynthetic, setIsSynthetic] = useState(false)

  const create = useMutation({
    mutationFn: () =>
      api.createTenant({
        id: id.trim().toLowerCase(),
        name: name.trim(),
        default_currency: currency.trim().toUpperCase() || 'INR',
        is_synthetic: isSynthetic,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      setId('')
      setName('')
      setCurrency('INR')
      setIsSynthetic(false)
    },
  })

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="grow">Create a tenant</h2>
      </div>
      <div className="card-body stack">
        {create.isError ? <ErrorCallout error={create.error} /> : null}
        <div className="row" style={{ gap: 12 }}>
          <label className="field grow">
            <span className="row tight nowrap" style={{ gap: 4 }}>
              Tenant ID
              <InfoTip text="Lowercase, unique, permanent - used everywhere data is scoped to this tenant. Cannot be changed after creation." />
            </span>
            <input
              placeholder="e.g. acme-media"
              value={id}
              onChange={(e) => setId(e.target.value)}
            />
          </label>
          <label className="field grow">
            Display name
            <input placeholder="e.g. Acme Media" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field" style={{ maxWidth: 100 }}>
            Currency
            <input
              maxLength={3}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              style={{ textTransform: 'uppercase' }}
            />
          </label>
        </div>
        <label className="row tight" style={{ gap: 6, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={isSynthetic}
            onChange={(e) => setIsSynthetic(e.target.checked)}
          />
          Sample data tenant
          <InfoTip text="Marks every figure this tenant serves as synthetic/generated (PRD 7.3) - use for demos and testing, never for a real client." />
        </label>
        <div>
          <button
            className="primary"
            disabled={!id.trim() || !name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create tenant'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function TenantAdmin() {
  const { me, loading: identityLoading } = useIdentity()
  const [activeOverride, setActiveOverride] = useState(() => getTenantOverride())
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: api.tenants,
    enabled: !!me?.permissions.includes('tenant:manage'),
  })

  if (identityLoading) {
    return (
      <main className="main">
        <div className="page">
          <Skeleton rows={4} />
        </div>
      </main>
    )
  }

  if (!me?.permissions.includes('tenant:manage')) {
    return (
      <main className="main">
        <div className="page">
          <Callout tone="danger" title="Superadmins only">
            Your role ({me?.role.replace(/_/g, ' ') ?? 'unknown'}) does not permit tenant
            management.
          </Callout>
        </div>
      </main>
    )
  }

  const switchTo = (tenantId: string | null) => {
    setTenantOverride(tenantId)
    setActiveOverride(tenantId)
    // Every query keyed by tenant-scoped data needs to be refetched under the
    // new X-Tenant-Id, and /auth/me itself reports a different tenant now -
    // a full reload is the simplest way to get every hook to that state.
    window.location.reload()
  }

  return (
    <main className="main">
      <div className="page">
        <div className="page-head">
          <div className="grow">
            <h1>Tenants</h1>
            <div className="page-sub">
              Create tenants and switch which one's data every other screen shows.
            </div>
          </div>
        </div>

        <Callout tone="info" title="You are viewing as">
          <span className="mono">{me.tenant_id}</span>
          {activeOverride ? (
            <>
              {' '}
              (switched by a superadmin){' '}
              <button className="small ghost" onClick={() => switchTo(null)}>
                Reset to home tenant
              </button>
            </>
          ) : (
            ' (your home tenant)'
          )}
          . Data health, scoring config and user management all follow this tenant.
        </Callout>

        <CreateTenantForm />

        <div className="card">
          <div className="card-head">
            <h2 className="grow">All tenants</h2>
          </div>
          {tenants.isLoading ? (
            <div className="card-body">
              <Skeleton rows={4} />
            </div>
          ) : tenants.isError ? (
            <div className="card-body">
              <ErrorCallout error={tenants.error} onRetry={() => void tenants.refetch()} />
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Currency</th>
                    <th>Type</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(tenants.data ?? []).map((t: TenantOut) => (
                    <tr key={t.id}>
                      <td className="small">
                        {t.name}
                        <div className="tiny mono faint">{t.id}</div>
                      </td>
                      <td className="mono tiny">{t.default_currency}</td>
                      <td>
                        {t.is_synthetic ? (
                          <Chip tone="synthetic">synthetic</Chip>
                        ) : (
                          <Chip tone="ok">live</Chip>
                        )}
                      </td>
                      <td>
                        {t.id === me.tenant_id ? (
                          <Chip tone="neutral">Currently viewing</Chip>
                        ) : (
                          <button className="small" onClick={() => switchTo(t.id)}>
                            Switch to this tenant
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
